import { Link } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { useStatePack } from '@/lib/state-context'

export default function SiteFooter() {
  const pack = useStatePack()

  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500 text-white">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <span className="text-lg font-semibold tracking-tight text-slate-900">
                ScamDefense
              </span>
            </div>
            <p className="mt-3 max-w-sm text-sm text-slate-500">
              Practical training to spot scams before they cost you. Backed by fraud
              prevention research.
            </p>
            {pack.sponsor && (
              <p className="mt-3 text-xs text-slate-400">{pack.sponsor.label}</p>
            )}
          </div>

          <FooterCol
            title="Basics"
            links={[
              { to: '/basics/new-age-of-scams', label: 'The New Age of Scams' },
              { to: '/basics/emotional-hijack', label: 'Emotional Hijack' },
              { to: '/basics/pause-and-verify', label: 'Pause and Verify' },
              { to: '/basics/warning-signs', label: 'Spot the Warning Signs' },
              { to: '/basics/digital-self-defense', label: 'Digital Self-Defense' },
              { to: '/basics/if-youre-scammed', label: 'If You’re Scammed' },
            ]}
          />

          <FooterCol
            title="Tools"
            links={[
              { to: '/tools/scam-checker', label: 'Scam Checker' },
              { to: '/tools/password-check', label: 'Password Check' },
              { to: '/tools/charity-checker', label: 'Charity Checker' },
              { to: '/scam-library', label: 'Scam Library' },
              { to: '/articles', label: 'Articles' },
            ]}
          />

          <FooterCol
            title="About"
            links={[
              { to: '/about', label: 'About ScamDefense' },
              { to: '/privacy', label: 'Privacy' },
              { to: '/privacy/opt-out', label: 'Do Not Sell or Share My Info' },
            ]}
          />
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-6 text-xs text-slate-400 sm:flex-row sm:justify-between">
          <p>&copy; {new Date().getFullYear()} ScamDefense. Educational content. Not legal or financial advice.</p>
          <p>If you are in immediate danger, call 911.</p>
        </div>
      </div>
    </footer>
  )
}

function FooterCol({
  title,
  links,
}: {
  title: string
  links: { to: string; label: string }[]
}) {
  return (
    <div>
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <ul className="mt-3 space-y-2 text-sm">
        {links.map(l => (
          <li key={l.to}>
            <Link to={l.to} className="text-slate-500 hover:text-slate-900">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
